import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";
import { showToast } from "./ToastSlice";

export const fetchAvailableCountries = createAsyncThunk(
  "didlogic/fetchAvailableCountries",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/numbers/countries");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load countries");
    }
  }
);

export const browseNumbers = createAsyncThunk(
  "didlogic/browseNumbers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/numbers/browse", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load numbers");
    }
  }
);

export const fetchKYCRequirements = createAsyncThunk(
  "didlogic/fetchKYCRequirements",
  async (country, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/numbers/kyc-requirements", { params: { country } });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch KYC info");
    }
  }
);

export const buyNumber = createAsyncThunk(
  "didlogic/buyNumber",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/didlogic/numbers/buy", payload);
      dispatch(showToast({ message: `Number +${payload.number} added to your account!`, variant: "success" }));
      return res.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Purchase failed", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message || "Purchase failed");
    }
  }
);

export const fetchMyNumbers = createAsyncThunk(
  "didlogic/fetchMyNumbers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/numbers/my", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch numbers");
    }
  }
);

export const fetchMyAgents = createAsyncThunk(
  "didlogic/fetchMyAgents",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/numbers/agents");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch agents");
    }
  }
);

export const assignNumberToAgent = createAsyncThunk(
  "didlogic/assignNumberToAgent",
  async ({ id, agentId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.put(`/didlogic/numbers/my/${id}/assign`, { agentId });
      const label = agentId ? "Number assigned to agent." : "Number unassigned from agent.";
      dispatch(showToast({ message: res.data.message || label, variant: "success" }));
      return { id, agentId, agent: res.data.agent || null };
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Assignment failed", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message || "Assignment failed");
    }
  }
);

export const releaseNumber = createAsyncThunk(
  "didlogic/releaseNumber",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/didlogic/numbers/my/${id}`);
      dispatch(showToast({ message: "Number released.", variant: "success" }));
      return id;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to release number", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const fetchCallRecords = createAsyncThunk(
  "didlogic/fetchCallRecords",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/call-records", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch call records");
    }
  }
);

export const fetchDIDTransactions = createAsyncThunk(
  "didlogic/fetchDIDTransactions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/didlogic/transactions", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch DID transactions");
    }
  }
);

const didlogicSlice = createSlice({
  name: "didlogic",
  initialState: {
    countries: [],
    countriesLoading: false,

    availableNumbers: [],
    availableTotal: 0,
    numbersLoading: false,
    currentKYC: null,

    buyingNumber: false,

    myNumbers: [],
    myNumbersTotal: 0,
    myNumbersLoading: false,

    myAgents: [],
    agentsLoading: false,
    assigning: false,

    callRecords: [],
    callPagination: {},
    callLoading: false,

    didTransactions: [],
    didTransactionsTotal: 0,
    transactionsLoading: false,

    error: null,
  },
  reducers: {
    clearBrowseNumbers(state) {
      state.availableNumbers = [];
      state.availableTotal = 0;
      state.currentKYC = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailableCountries.pending, (s) => { s.countriesLoading = true; })
      .addCase(fetchAvailableCountries.fulfilled, (s, a) => { s.countriesLoading = false; s.countries = a.payload.data || []; })
      .addCase(fetchAvailableCountries.rejected, (s) => { s.countriesLoading = false; })

      .addCase(browseNumbers.pending, (s) => { s.numbersLoading = true; })
      .addCase(browseNumbers.fulfilled, (s, a) => {
        s.numbersLoading = false;
        s.availableNumbers = a.payload.data || [];
        s.availableTotal = a.payload.total || 0;
        s.currentKYC = a.payload.kyc || null;
      })
      .addCase(browseNumbers.rejected, (s) => { s.numbersLoading = false; })

      .addCase(fetchKYCRequirements.fulfilled, (s, a) => { s.currentKYC = a.payload.data || null; })

      .addCase(buyNumber.pending, (s) => { s.buyingNumber = true; })
      .addCase(buyNumber.fulfilled, (s) => { s.buyingNumber = false; })
      .addCase(buyNumber.rejected, (s) => { s.buyingNumber = false; })

      .addCase(fetchMyNumbers.pending, (s) => { s.myNumbersLoading = true; })
      .addCase(fetchMyNumbers.fulfilled, (s, a) => {
        s.myNumbersLoading = false;
        s.myNumbers = a.payload.data || [];
        s.myNumbersTotal = a.payload.total || 0;
      })
      .addCase(fetchMyNumbers.rejected, (s) => { s.myNumbersLoading = false; })

      .addCase(fetchMyAgents.pending, (s) => { s.agentsLoading = true; })
      .addCase(fetchMyAgents.fulfilled, (s, a) => { s.agentsLoading = false; s.myAgents = a.payload.data || []; })
      .addCase(fetchMyAgents.rejected, (s) => { s.agentsLoading = false; })

      .addCase(assignNumberToAgent.pending, (s) => { s.assigning = true; })
      .addCase(assignNumberToAgent.fulfilled, (s, a) => {
        s.assigning = false;
        const { id, agentId, agent } = a.payload;
        const num = s.myNumbers.find((n) => n._id === id);
        if (num) {
          num.assignedAgentId = agentId ? { _id: agentId, ...agent } : null;
          num.assignedAgentAt = agentId ? new Date().toISOString() : null;
        }
      })
      .addCase(assignNumberToAgent.rejected, (s) => { s.assigning = false; })

      .addCase(releaseNumber.fulfilled, (s, a) => {
        s.myNumbers = s.myNumbers.filter((n) => n._id !== a.payload);
        s.myNumbersTotal = Math.max(0, s.myNumbersTotal - 1);
      })

      .addCase(fetchCallRecords.pending, (s) => { s.callLoading = true; })
      .addCase(fetchCallRecords.fulfilled, (s, a) => {
        s.callLoading = false;
        s.callRecords = a.payload.data || [];
        s.callPagination = a.payload.pagination || {};
      })
      .addCase(fetchCallRecords.rejected, (s) => { s.callLoading = false; })

      .addCase(fetchDIDTransactions.pending, (s) => { s.transactionsLoading = true; })
      .addCase(fetchDIDTransactions.fulfilled, (s, a) => {
        s.transactionsLoading = false;
        s.didTransactions = a.payload.data || [];
        s.didTransactionsTotal = a.payload.total || 0;
      })
      .addCase(fetchDIDTransactions.rejected, (s) => { s.transactionsLoading = false; });
  },
});

export const { clearBrowseNumbers } = didlogicSlice.actions;
export default didlogicSlice.reducer;
